/**
 * Where the app actually lives. Issue #1703.
 *
 * One home for both store URLs, because before this the App Store link was a
 * literal inside `AppStoreNudge.tsx` and there was no Play link anywhere in the
 * repo at all — which is why Android web visitors were offered nothing.
 */

/**
 * The iOS App Store id.
 *
 * ✅ CONFIRMED by Kasandra against App Store Connect, 2026-09-27. This comment said
 * "not verified" while it was not, which is why the confirmation is dated rather than
 * assumed — a wrong id here renders a native Smart App Banner for someone else's app,
 * and it is invisible in code review either way.
 *
 * `installBanner.test.tsx` pins this constant against `index.html`'s meta tag, so the
 * two statements of the same fact cannot drift apart.
 */
export const IOS_APP_ID = "6763621685";

export const APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_ID}`;

/**
 * Google Play keys on the Android package name, which is the bundle id CLAUDE.md
 * records for the Median shell: `com.kinyoudoitall.kin`.
 */
export const ANDROID_PACKAGE = "com.kinyoudoitall.kin";

export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * ⚠️ NO TRACKING PARAMETERS, deliberately.
 *
 * #1703 says the UTM/`ct` conventions already exist (Flodesk Founding Waitlist
 * page, July 12 notes) and to reuse them rather than invent new ones. Those notes
 * are not in this repo — like several audit documents, they live on the Desktop —
 * and nothing in `src/` carries a store-link convention to copy.
 *
 * So the links ship bare rather than with an invented scheme, which would be worse
 * than none: a second convention makes install attribution ambiguous instead of
 * absent, and an ambiguous number gets quoted. Adding them later is a one-line
 * change to the two constants above.
 */
export const storeUrlFor = (target: "ios" | "android"): string =>
  target === "ios" ? APP_STORE_URL : PLAY_STORE_URL;

/** Store name for user-facing copy. */
export const storeNameFor = (target: "ios" | "android"): string =>
  target === "ios" ? "App Store" : "Google Play";

/**
 * The store as it appears INSIDE a sentence, preposition included.
 *
 * ⚠️ This exists because `storeNameFor` is not substitutable into prose, and using it
 * there produced "Kin's in App Store!" — the reviewed copy said "in THE App Store",
 * and the article was lost in the swap. The two stores also take different
 * prepositions: you are *in the* App Store but *on* Google Play. One string per store
 * keeps both correct instead of assembling English out of parts.
 *
 * ⚠️ Caught by the Playwright spec, NOT by vitest. The unit tests exercised
 * `storeNameFor` in isolation and never rendered the sentence it lands in, so they
 * structurally could not see it. `installBanner.test.ts` now renders the component.
 */
export const storeLocationPhrase = (target: "ios" | "android"): string =>
  target === "ios" ? "in the App Store" : "on Google Play";
